import { coercePaymentLohnarten } from './models/Company'
import { oneTimePaymentType, OneTimePaymentType } from './models/OneTimePayment'

/**
 * A Lohnart as LODAS reads `lohnart_nr`: a positive whole number. Anything else
 * is treated as missing rather than written under a wage type nobody chose.
 */
export const asLohnart = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : null

/**
 * The Lohnart the LODAS export books each one-time payment type under.
 *
 * Several Lohnarten may name one payment type, and the first entry given for a
 * type is the one written out; later entries of the same type are ignored. The
 * legacy `commissionLohnart` fills in Commission when the list names none.
 */
export const lodasPaymentLohnarten = (
  datev:
    | { paymentLohnarten?: unknown; commissionLohnart?: number | null }
    | null
    | undefined
): Partial<Record<OneTimePaymentType, number>> => {
  const payments: Partial<Record<OneTimePaymentType, number>> = {}
  for (const entry of coercePaymentLohnarten(datev?.paymentLohnarten)) {
    if (!(oneTimePaymentType as readonly string[]).includes(entry?.type)) {
      continue
    }
    const type = entry.type as OneTimePaymentType
    const lohnart = asLohnart(entry?.lohnart)
    if (lohnart !== null && payments[type] === undefined) {
      payments[type] = lohnart
    }
  }

  const legacyCommission = asLohnart(datev?.commissionLohnart)
  if (payments.Commission === undefined && legacyCommission !== null) {
    payments.Commission = legacyCommission
  }

  return payments
}

/** Which other setting the salary Lohnart shares its number with. */
export type SalaryLohnartCollision =
  | { lohnart: number; with: 'vacation' | 'sickness' }
  | { lohnart: number; with: 'payment'; type: OneTimePaymentType }

/**
 * The setting the salary Lohnart collides with, or null.
 *
 * A wage type carries one meaning, so the export drops the salary when its
 * number is also used for continued pay or for the Lohnart a payment type is
 * booked under. Only the first entry per payment type counts, because only
 * that one is ever written out.
 */
export const findSalaryLohnartCollision = (
  salary: number | null,
  {
    vacation,
    sickness,
    payments,
  }: {
    vacation: number | null
    sickness: number | null
    payments: Partial<Record<OneTimePaymentType, number>>
  }
): SalaryLohnartCollision | null => {
  if (salary === null) {
    return null
  }
  if (salary === vacation) {
    return { lohnart: salary, with: 'vacation' }
  }
  if (salary === sickness) {
    return { lohnart: salary, with: 'sickness' }
  }
  const type = (Object.keys(payments) as OneTimePaymentType[]).find(
    (key) => payments[key] === salary
  )
  return type ? { lohnart: salary, with: 'payment', type } : null
}

/**
 * Whether the LODAS Stammdaten export writes a monthly salary Festbezug for
 * this contract, provided the company has a usable salary Lohnart.
 */
export const carriesMonthlySalary = (contract: any): boolean =>
  contract?.remuneration?.type === 'fixed' &&
  contract?.remuneration?.salary != null
